import { useMemo } from "react";
import { resolveDomainCode } from "../utils/bigquery";

export const useGroupedItems = (itemsList, params, subCategory) => {
    return useMemo(() => {
        const groupedItems = {};
        itemsList.forEach((indicator) => {
            // Only process indicators that match the categoryToFilter
            if (indicator.slug === subCategory) {
                const category = indicator.label;
                if (!groupedItems[category]) {
                    groupedItems[category] = [];
                }

                indicator.items.forEach((item) => {
                    let queryParams = new URLSearchParams();

                    queryParams.append("element_code", item.element_code);
                    queryParams.append("item_code", item.item_code);
                    //queryParams.append("foodgroup", item.foodGroup);

                    const domain_code = resolveDomainCode(item.domain_code);
                    const sex_code = item.sex_code ? item.sex_code : false;
                    // The SDG views disaggregate by a `sex` label ("Both sexes"/"Male"/"Female")
                    // rather than the sex_code column the FAOSTAT views use, and without picking
                    // one the chart draws all three stacked on the same series.
                    const sex = item.sex ? item.sex : false;
                    const urban_rural = item.urban_rural ? item.urban_rural : false;
                    const sort_by_item = item.sort_by_item ? item.sort_by_item : false;
                    const sort_by_element = item.sort_by_element ? item.sort_by_element : false;
                    const indicator_code = item.indicator_code ? item.indicator_code : false;
                    const foodGroup = item.food_group ? item.food_group : false;

                    let chart_typeParams = { ...params };

                    // For certain chart types, only include the country itself (not regional comparisons)
                    // params.area structure: [countryCode, ...regionalCodes]
                    if (item.chart_type === 'stackedBar' || item.chart_type === 'pie' || item.chart_type === 'donut') {
                        chart_typeParams.area = params.area.slice(0, 1); // Keep only the country code
                    }

                    // Helper function to format codes based on their type
                    const formatCodes = (codes) => {
                        if (!codes || codes.length === 0) return [];
                        return codes.map(code => {
                            // Check if the original value is a number
                            if (typeof code === 'number') {
                                return code; // No quotes for numbers
                            } else {
                                return `'${code}'`; // Single quotes for strings
                            }
                        });
                    };

                    const elementCodes = formatCodes(item.element_code);
                    const itemCodes = formatCodes(item.item_code);

                    // A handful of domains store area_code_m49 as a STRING column rather than
                    // INT64 (confirmed per-domain via BigQuery's INFORMATION_SCHEMA); mockData.js
                    // flags those with area_code_is_string so the literal gets quoted accordingly.
                    const areaCodes = item.area_code_is_string
                        ? chart_typeParams.area.map((code) => `"${code}"`)
                        : chart_typeParams.area.map((code) => `${code}`);

                    // Built from areaCodes, not from the raw codes: on a STRING column an
                    // unquoted literal makes BigQuery reject the whole statement with
                    // "No matching signature for operator CASE".
                    const areaOrderCase = areaCodes
                        .map((code, index) => `WHEN ${code} THEN ${index + 1}`)
                        .join(" ");

                    const foodGroupOrderCase = foodGroup
                        ? `CASE food_group_code ${foodGroup
                            .map((code, index) => `WHEN "${code}" THEN ${index + 1}`)
                            .join(" ")} ELSE 9999 END,`
                        : "";

                    const sexOrderCase = sex_code
                        ? `CASE sex_code ${sex_code
                            .map((code, index) => `WHEN "${code}" THEN ${index + 1}`)
                            .join(" ")} ELSE 9999 END,`
                        : "";

                    const itemCodeOrderCase = sort_by_item
                        ? `CASE item_code ${itemCodes
                            .map((code, index) => `WHEN ${code} THEN ${index + 1}`)
                            .join(" ")} ELSE 9999 END,`
                        : "";

                    const elementCodeOrderCase = sort_by_element
                        ? `CASE element_code ${elementCodes
                            .map((code, index) => `WHEN ${code} THEN ${index + 1}`)
                            .join(" ")} ELSE 9999 END,`
                        : "";

                    // Every constraint except the year, shared by the query and - for line
                    // charts - by the subquery below.
                    const dimensionFilters = `
        ${elementCodes && elementCodes.length > 0
                            ? `AND element_code IN (${elementCodes.join(", ")}) `
                            : ``
                        }
        ${itemCodes && itemCodes.length > 0
                            ? `AND item_code IN (${itemCodes.join(", ")}) `
                            : ``
                        }
        ${indicator_code
                            ? `AND indicator_code IN (${indicator_code
                                .map((indicator_code) => `"${indicator_code}"`)
                                .join(", ")}) `
                            : ``
                        }
        ${foodGroup
                            ? `AND food_group_code IN (${foodGroup
                                .map((food_group) => `"${food_group}"`)
                                .join(", ")}) `
                            : ``
                        }
      ${sex_code
                            ? `AND sex_code IN (${sex_code
                                .map((sex_code) => `"${sex_code}"`)
                                .join(", ")}) `
                            : ""
                        }
      ${sex
                            ? `AND sex IN (${sex
                                .map((value) => `"${value}"`)
                                .join(", ")}) `
                            : ""
                        }
      ${urban_rural
                            ? `AND urban_rural IN (${urban_rural
                                .map((urban_rural) => `"${urban_rural}"`)
                                .join(", ")}) `
                            : ""
                        }`;

                    // No source publishes the reference period: not the FAOSTAT SDG domain, not
                    // the UNSD one, not the FAOSTAT bulk notes (all checked). For indicators that
                    // measure a change *between assessments* it is implicit in the indicator
                    // itself - the span since the previous reading - so the query hands back that
                    // previous year and the chart derives the period from it. Marked as derived
                    // in the alias so nobody mistakes it for a column of the view.
                    //
                    // It has to come from the query because that reading usually falls outside
                    // the selected range: 15.2.1 reports 2000, 2015 and 2025, so on the default
                    // range the only point on the chart has no predecessor to measure from. The
                    // window runs over the indicator's whole history and only then gets filtered
                    // down to the selected years; the subquery repeats every filter except the
                    // year, without which it would scan the entire view. Every other indicator
                    // keeps exactly the query it had.
                    const partitionColumns = [
                        "area_code_m49",
                        elementCodes.length > 0 ? "element_code" : null,
                        itemCodes.length > 0 ? "item_code" : null,
                        indicator_code ? "indicator_code" : null,
                        foodGroup ? "food_group_code" : null,
                        sex_code ? "sex_code" : null,
                        sex ? "sex" : null,
                        urban_rural ? "urban_rural" : null,
                    ].filter(Boolean).join(", ");

                    // The window has to run over distinct reported years, not over rows: any view
                    // with more than one row per year - duplicated area names in the SDG views,
                    // sex or urban/rural breakdowns elsewhere - would otherwise return the row
                    // next door, i.e. the same year, and the period would come out as 2020-2020.
                    const periodKeys = `${partitionColumns}, year`;
                    const source = item.reports_period
                        ? `(
        SELECT v.*, p.derived_period_start
        FROM ${domain_code} v
        LEFT JOIN (
          SELECT ${periodKeys},
                 LAG(year) OVER (PARTITION BY ${partitionColumns} ORDER BY year) AS derived_period_start
          FROM (
            SELECT DISTINCT ${periodKeys}
            FROM ${domain_code}
            WHERE area_code_m49 IN (${areaCodes.join(", ")}) ${dimensionFilters}
            AND value IS NOT NULL AND CAST(value AS STRING) != ""
          )
        ) p USING (${periodKeys.split(", ").join(", ")})
        WHERE area_code_m49 IN (${areaCodes.join(", ")}) ${dimensionFilters}
      )`
                        : domain_code;

                    const sqlQuery = `SELECT * FROM ${source}
      WHERE area_code_m49 IN (${areaCodes.join(", ")})
      ${item.chart_type === "status"
                            ? "" // Skip year filter for status charts
                            : `AND year IN (${chart_typeParams.year // years
                                .map((year) => `"${year}"`)
                                .join(", ")}) `
                        }${item.reports_period ? "" : dimensionFilters}
      ORDER BY 
        ${foodGroupOrderCase}
        ${elementCodeOrderCase}
        ${sexOrderCase}
        ${itemCodeOrderCase}
        CASE area_code_m49 
          ${areaOrderCase} 
          ELSE 9999 
        END
      LIMIT 30000`;


                    groupedItems[category].push({
                        query: sqlQuery,
                        // Augment info with the selected years so chart transforms can align x-axis ticks
                        info: { ...item, selectedYears: chart_typeParams.year },
                        title: item.label,
                        category: category,
                    });
                });
            }
        });
        return groupedItems;
    }, [itemsList, params, subCategory]);
};
